/**
 * Room/Bed Data Model
 * Represents individual rooms and beds within a ward/unit
 * Links to Ward -> Facility hierarchy and is referenced by Resident (admission.roomNumber)
 */

export const ROOM_TYPES = {
  SINGLE: 'Single',
  SHARED: 'Shared',
  PREMIUM: 'Premium',
  DEMENTIA_SPECIFIC: 'Dementia-specific',
  PALLIATIVE: 'Palliative',
  RESPITE: 'Respite',
  ISOLATION: 'Isolation'
};

export const ROOM_STATUS = {
  AVAILABLE: 'available',
  OCCUPIED: 'occupied',
  RESERVED: 'reserved',
  MAINTENANCE: 'maintenance',
  CLOSED: 'closed'
};

export const BED_STATUS = {
  AVAILABLE: 'available',
  OCCUPIED: 'occupied',
  RESERVED: 'reserved',
  OUT_OF_SERVICE: 'out_of_service'
};

export const ROOM_AMENITIES = [
  'Ensuite bathroom',
  'Shared bathroom',
  'Kitchenette',
  'Balcony/Patio',
  'Garden access',
  'TV',
  'Phone line',
  'WiFi',
  'Nurse call system',
  'Ceiling hoist',
  'Bariatric equipment',
  'Oxygen outlet',
  'Suction outlet',
  'Air conditioning',
  'Wheelchair accessible'
];

export const createBed = (data = {}) => ({
  id: data.id || `BED-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
  label: data.label || 'A',
  status: data.status || BED_STATUS.AVAILABLE,
  residentId: data.residentId || null,
  residentName: data.residentName || null,
  specialEquipment: data.specialEquipment || [],
  notes: data.notes || ''
});

export const createRoom = (data = {}) => {
  const beds = data.beds || [];
  const occupiedBeds = beds.filter(b => b.status === BED_STATUS.OCCUPIED).length;

  return {
    id: data.id || `ROOM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    facilityId: data.facilityId || null,
    wardId: data.wardId || null,

    roomNumber: data.roomNumber || '',
    roomName: data.roomName || '',
    floor: data.floor || '1',
    wing: data.wing || '',

    roomType: data.roomType || ROOM_TYPES.SINGLE,
    status: data.status || ROOM_STATUS.AVAILABLE,

    beds: beds.map(b => createBed(b)),
    totalBeds: beds.length || data.totalBeds || 1,
    occupiedBeds,
    availableBeds: (beds.length || data.totalBeds || 1) - occupiedBeds,

    amenities: data.amenities || [],

    nfcTagId: data.nfcTagId || null,
    qrCode: data.qrCode || null,
    bleBeaconId: data.bleBeaconId || null,

    dailyRate: data.dailyRate || null,
    accommodationCategory: data.accommodationCategory || null,

    lastCleanedAt: data.lastCleanedAt || null,
    lastInspectedAt: data.lastInspectedAt || null,
    maintenanceNotes: data.maintenanceNotes || '',

    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString()
  };
};

export default { createRoom, createBed, ROOM_TYPES, ROOM_STATUS, BED_STATUS, ROOM_AMENITIES };
